import { useState, useRef, useEffect, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";

export interface CustomDatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}

export function CustomDatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = "请选择日期",
  required = false,
  disabled = false,
  className = "",
  "aria-label": ariaLabel,
}: CustomDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<"bottom" | "top">("bottom");
  const [alignRight, setAlignRight] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse initial view year and month (0-indexed month)
  const initialDate = value ? parseDate(value) : new Date();
  const [viewYear, setViewYear] = useState<number>(initialDate ? initialDate.getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(initialDate ? initialDate.getMonth() : new Date().getMonth());

  // Update view when value changes
  useEffect(() => {
    if (value) {
      const d = parseDate(value);
      if (d) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    }
  }, [value]);

  const updatePlacement = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Popover is ~320px tall and 280px wide
      const popoverWidth = 280;
      setAlignRight(rect.left + popoverWidth > window.innerWidth - 16);

      // Prefer opening downwards unless there is genuinely not enough space in viewport and above has more room
      if (spaceBelow < 330 && spaceAbove > spaceBelow) {
        setPlacement("top");
      } else {
        setPlacement("bottom");
      }
    }
  };

  const handleToggle = (e?: ReactMouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;
    if (!isOpen) {
      updatePlacement();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: globalThis.MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handlePrevMonth = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleSelectDate = (e: ReactMouseEvent, dateStr: string) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(dateStr);
    setIsOpen(false);
  };

  const handleToday = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const today = formatDate(new Date());
    if (isDateDisabled(today, min, max)) return;
    onChange(today);
    setIsOpen(false);
  };

  const handleClear = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (required) return;
    onChange("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      handleToggle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
    }
  };

  const todayStr = formatDate(new Date());
  const calendarDays = generateCalendarGrid(viewYear, viewMonth);

  return (
    <div
      ref={containerRef}
      className={`custom-picker-container custom-date-picker-container ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""} ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Hidden input for form integration & test compatibility */}
      <input
        type="date"
        className="custom-picker-native-input"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        required={required}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />

      <button
        type="button"
        className="custom-picker-trigger"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={ariaLabel || placeholder}
      >
        <span className="custom-picker-value">
          {value ? (
            <span className="custom-picker-text">{value}</span>
          ) : (
            <span className="custom-picker-placeholder">{placeholder}</span>
          )}
        </span>
        <CalendarIcon size={16} className="custom-picker-icon" aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className={`custom-calendar-popover placement-${placement} ${alignRight ? "align-right" : ""}`}
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with Month/Year & Horizontal Chevron Arrows */}
          <div className="calendar-header">
            <span className="calendar-header-title">
              {viewYear}年{String(viewMonth + 1).padStart(2, "0")}月
            </span>
            <div className="calendar-nav-buttons">
              <button
                type="button"
                className="calendar-nav-btn"
                onClick={handlePrevMonth}
                title="上一月"
                aria-label="上一月"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                className="calendar-nav-btn"
                onClick={handleNextMonth}
                title="下一月"
                aria-label="下一月"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Week Headers */}
          <div className="calendar-grid-header">
            {["一", "二", "三", "四", "五", "六", "日"].map((w) => (
              <span key={w} className="calendar-week-cell">
                {w}
              </span>
            ))}
          </div>

          {/* Days Grid */}
          <div className="calendar-grid" role="grid">
            {calendarDays.map((item, idx) => {
              const isSelected = item.dateStr === value;
              const isToday = item.dateStr === todayStr;
              const isDisabled = isDateDisabled(item.dateStr, min, max);

              return (
                <button
                  key={`${item.dateStr}-${idx}`}
                  type="button"
                  disabled={isDisabled}
                  className={`calendar-cell ${isSelected ? "selected" : ""} ${isToday ? "is-today" : ""} ${
                    item.isCurrentMonth ? "" : "outside-month"
                  } ${isDisabled ? "disabled" : ""}`}
                  onClick={(e) => !isDisabled && handleSelectDate(e, item.dateStr)}
                >
                  <span className="cell-number">{item.day}</span>
                </button>
              );
            })}
          </div>

          {/* Footer Actions */}
          <div className="calendar-footer">
            {!required && (
              <button type="button" className="calendar-action-btn clear-btn" onClick={handleClear}>
                清除
              </button>
            )}
            <button
              type="button"
              className="calendar-action-btn today-btn"
              onClick={handleToday}
              disabled={isDateDisabled(todayStr, min, max)}
            >
              今天
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function parseDate(str: string): Date | null {
  if (!str) return null;
  const parts = str.split("-");
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
  return new Date(y, m, d);
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isDateDisabled(dateStr: string, min?: string, max?: string): boolean {
  if (min && dateStr < min) return true;
  if (max && dateStr > max) return true;
  return false;
}

interface CalendarGridDay {
  dateStr: string;
  day: number;
  isCurrentMonth: boolean;
}

function generateCalendarGrid(year: number, month: number): CalendarGridDay[] {
  const days: CalendarGridDay[] = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  let startDayOfWeek = firstDay.getDay();
  startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const d = prevMonthLastDay - i;
    const prevDate = new Date(year, month - 1, d);
    days.push({
      dateStr: formatDate(prevDate),
      day: d,
      isCurrentMonth: false,
    });
  }

  const totalDays = lastDay.getDate();
  for (let d = 1; d <= totalDays; d++) {
    const currDate = new Date(year, month, d);
    days.push({
      dateStr: formatDate(currDate),
      day: d,
      isCurrentMonth: true,
    });
  }

  const remaining = 7 - (days.length % 7);
  if (remaining < 7) {
    for (let d = 1; d <= remaining; d++) {
      const nextDate = new Date(year, month + 1, d);
      days.push({
        dateStr: formatDate(nextDate),
        day: d,
        isCurrentMonth: false,
      });
    }
  }

  return days;
}
